import { defaultBadgeFormSettings, defaultBadgeSampleAccountData } from '@/backend/features/badging/badging-configuration/shared';
import { canViewLogiReports } from '@/backend/features/logi-reports/utils';
import {
    accountsLinks,
    accountsRoutes,
    announcementsRoutes,
    documentsRoutes,
    groupsRoutes,
    missionsRoutes,
    organizationRoutes,
    reportsRoutes,
    screeningsRoutes,
    timeTrackingRoutes,
} from '@/backend/routes';
import { extendAccountBadgeTableColumns } from '@/ui/features/badging/badging-customizations/account-badge/extendAccountBadgeTableColumns';
import { useAccountBadgeSlideout } from '@/ui/features/badging/badging-customizations/account-badge/hooks/useAccountBadgeSlideout';
import { adjustAccountSelectionChanged } from '@/ui/features/badging/badging-customizations/account/adjustAccountSelectionChanged';
import { extendAccountSelectionOptionComponents } from '@/ui/features/badging/badging-customizations/account/extendAccountSelectionOptionComponents';
import { extendAccountSelectionSelectedColumns } from '@/ui/features/badging/badging-customizations/account/extendAccountSelectionSelectedColumns';
import { getExtendRmsAccountFilters } from '@/ui/features/badging/badging-customizations/account/extendRmsAccountFilters';
import { mapAccountToAccountSelection } from '@/ui/features/badging/badging-customizations/account/mapAccountToAccountSelection';
import { RmsBadgeCreate } from '@/ui/features/badging/badging-customizations/badge-create/RmsBadgeCreate';
import { filterAccountSections } from '@/ui/utils/filterAccountSections';
import { useAlertsRoutes } from '@juvare/alert-ui/ui/hooks';
import { requireBaseBadgesPermissions } from '@juvare/badging/backend/features/badges/shared';
import { ManageBadgesPanel } from '@juvare/badging/ui/features/badge-management';
import { FilterConfigBuilder } from '@juvare/common-data-table-ui/FiltersBar/utils';
import { GlobalAppRouteObject } from '@juvare/common-web-ui/GlobalApp';
import {
    allowAccessToAccounts,
    allowAccessToGroups,
    allowAccessToUserDirectory,
    requireAnyOrgPermission,
    requireAnySystemPermission,
} from '@juvare/contact-management/backend/features/permissions/shared';
import { AccountFilterData, AccountPage } from '@juvare/contact-management/ui/features/accounts';
import { UserDirectoryPanel } from '@juvare/contact-management/ui/features/user-directory';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { hasEnabledFeature } from '@juvare/contact-management/utils';
import { allowViewScreenings } from '@juvare/credentialing/backend/features/permissions';
import { DocumentPanel } from '@juvare/document-library/ui/features/file-manager';
import { LogiPanel } from '@juvare/logi-reports/ui/features/logi-panel';
import { UserState } from '@juvare/platform-sdk-react/types';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import BadgeIcon from '@mui/icons-material/Badge';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import GroupIcon from '@mui/icons-material/Group';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HomeIcon from '@mui/icons-material/Home';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LanIcon from '@mui/icons-material/Lan';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined';
import RememberMeIcon from '@mui/icons-material/RememberMe';
import RememberMeOutlinedIcon from '@mui/icons-material/RememberMeOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavigateFunction } from 'react-router';
import { HomePage } from './components';
import { RmsAccountPanel, RmsAccountProfileHeader } from './features/accounts';
import { AnnouncementsForm } from './features/announcements';
import { BackgroundCheckRequiredInformationModal } from './features/background-check/components/background-check-required-information-modal/BackgroundCheckRequiredInformationModal';
import { getTemplateDataForBadgeClassifierImageGeneration } from './features/badging/badging-configuration/getTemplateDataForBadgeClassifierImageGeneration';
import { GroupsPage } from './features/groups';
import { MissionsPage } from './features/missions';
import { OrganizationsPage } from './features/organizations';
import { ScreeningsRouter } from './features/screenings';
import { RmsTimeTrackingPage } from './features/time-tracking';

export function useUserAppRoutes() {
    const { createGlobalMenuRoutes } = useGlobalMenuRoutes();
    const userState = useUserState();

    const createUserAppRoutes = React.useCallback((): GlobalAppRouteObject[] => {
        const routes: GlobalAppRouteObject[] = [];
        createGlobalMenuRoutes(routes);
        createAnnouncementsRoutes(routes, userState);
        createBackgroundCheckRoutes(routes);

        return routes;
    }, [createGlobalMenuRoutes, userState]);

    return { createUserAppRoutes };
}

function useGlobalMenuRoutes() {
    const { t } = useTranslation('rms');
    const settings = useSelector(getSettings);
    const { buildAccountBadgeSlideoutComponent } = useAccountBadgeSlideout();
    const alertsRoutes = useAlertsRoutes();
    const userState = useUserState();

    const createGlobalMenuRoutes = React.useCallback(
        (routes: GlobalAppRouteObject[]): GlobalAppRouteObject[] => {
            // Everybody has access to Home page
            routes.push({
                index: true,
                element: <HomePage />,
                handle: {
                    type: 'page',
                    label: t('pages.home.title'),
                    icon: <HomeOutlinedIcon />,
                    iconSelected: <HomeIcon />,
                },
            });

            // Everybody has access to Notifications / Inbox page, other Notifications pages require special permissions
            routes.push(...alertsRoutes);

            // Special permissions required for Accounts page
            if (allowAccessToAccounts(userState)) {
                routes.push({
                    path: accountsRoutes.root + '/*',
                    element: <RmsAccountPanel />,
                    handle: {
                        type: 'page',
                        onClick: (navigate: NavigateFunction) => {
                            const urlParams = new URLSearchParams();
                            urlParams.set('filter', JSON.stringify({ systemStatus: ['active'] }));
                            navigate({ pathname: accountsLinks.root, search: urlParams.toString() });
                        },
                        label: t('pages.accounts.title'),
                        icon: <PermContactCalendarOutlinedIcon />,
                        iconSelected: <PermContactCalendarIcon />,
                    },
                });
            } else {
                // If no permissions for Accounts page, then at least allow the account profile page
                routes.push({
                    path: accountsRoutes.root + '/:accountId',
                    element: (
                        <AccountPage
                            customAccountProfileHeaderComponent={RmsAccountProfileHeader}
                            onFilterAccountSections={(sections) => filterAccountSections(userState, sections)}
                        />
                    ),
                    children: [{ path: '*', element: <AccountPage /> }],
                });
            }

            if (allowAccessToUserDirectory(userState) && hasEnabledFeature(userState, 'userDirectory')) {
                routes.push({
                    path: 'user-directory/*',
                    element: <UserDirectoryPanel />,
                    handle: {
                        type: 'page',
                        label: t('pages.userDirectory.title'),
                        icon: <FolderSharedOutlinedIcon />,
                        iconSelected: <FolderSharedIcon />,
                    },
                });
            }

            // Special permissions required for Groups page
            if (allowAccessToGroups(userState)) {
                routes.push({
                    path: groupsRoutes.root + '/*',
                    element: <GroupsPage />,
                    handle: {
                        type: 'page',
                        label: t('pages.groups.title'),
                        icon: <GroupOutlinedIcon />,
                        iconSelected: <GroupIcon />,
                    },
                });
            }

            // Special permissions required for Organizations page
            if (requireAnySystemPermission(userState, 'view-organizations')) {
                routes.push({
                    path: organizationRoutes.root + '/*',
                    element: <OrganizationsPage />,
                    handle: {
                        type: 'page',
                        label: t('pages.organizations.title'),
                        icon: <LanOutlinedIcon />,
                        iconSelected: <LanIcon />,
                    },
                });
            }

            // Only admins have access to Administration page
            if (allowViewScreenings(userState)) {
                routes.push({
                    path: screeningsRoutes.root + '/*',
                    element: <ScreeningsRouter />,
                    handle: {
                        type: 'page',
                        label: t('pages.screenings.title'),
                        icon: <BadgeOutlinedIcon />,
                        iconSelected: <BadgeIcon />,
                    },
                });
            }

            // Everybody has access to Missions page
            routes.push({
                path: missionsRoutes.root + '/*',
                element: <MissionsPage />,
                handle: {
                    type: 'page',
                    label: t('pages.missions.title'),
                    icon: <FlagOutlinedIcon />,
                    iconSelected: <FlagIcon />,
                },
            });

            // Everybody has access to Time Tracking page
            routes.push({
                path: timeTrackingRoutes.root + '/*',
                element: <RmsTimeTrackingPage />,
                handle: {
                    type: 'page',
                    label: t('pages.timeTracking.title'),
                    icon: <UpdateOutlinedIcon />,
                    iconSelected: <UpdateIcon />,
                },
            });

            if (requireAnyOrgPermission(userState, 'manage-documents', 'view-documents')) {
                routes.push({
                    path: documentsRoutes.root,
                    element: <DocumentPanel />,
                    children: [{ path: '*', element: <DocumentPanel /> }],
                    handle: {
                        type: 'page',
                        label: t('pages.documents.title'),
                        icon: <FolderOutlinedIcon />,
                        iconSelected: <FolderIcon />,
                    },
                });
            }

            if (userState.isLogiEnabled && canViewLogiReports(userState)) {
                routes.push({
                    path: reportsRoutes.root,
                    element: <LogiPanel />,
                    handle: {
                        type: 'page',
                        label: t('pages.reports.title'),
                        icon: <BarChartOutlinedIcon />,
                        iconSelected: <BarChartIcon />,
                    },
                    children: [
                        {
                            path: '*',
                            element: <LogiPanel />,
                        },
                    ],
                });
            }

            if (userState.badging.badgingEnabled && requireBaseBadgesPermissions(userState)) {
                routes.push({
                    path: 'badges/*',
                    element: (
                        <ManageBadgesPanel
                            onGetTemplateDataForBadgeClassifierImageGeneration={getTemplateDataForBadgeClassifierImageGeneration}
                            defaultSampleAccountData={defaultBadgeSampleAccountData}
                            defaultFormSettings={defaultBadgeFormSettings}
                            BadgeCreateComponent={RmsBadgeCreate}
                            // account badge
                            extendAccountBadgeTableColumns={(defaultColumns) =>
                                extendAccountBadgeTableColumns(defaultColumns, t, settings.systemRoleMap)
                            }
                            // account
                            mapAccountToAccountSelection={mapAccountToAccountSelection}
                            adjustAccountSelectionChanged={adjustAccountSelectionChanged}
                            extendAddAccountFilters={(builder: FilterConfigBuilder<AccountFilterData>) =>
                                getExtendRmsAccountFilters(builder, t)
                            }
                            extendAccountSelectionOptionComponents={extendAccountSelectionOptionComponents}
                            extendAccountSelectionSelectedColumns={(setAccounts) => extendAccountSelectionSelectedColumns(setAccounts, t)}
                            buildAccountBadgeSlideoutComponent={buildAccountBadgeSlideoutComponent}
                        />
                    ),
                    handle: {
                        type: 'page',
                        label: t('pages.badges.title'),
                        icon: <RememberMeOutlinedIcon />,
                        iconSelected: <RememberMeIcon />,
                    },
                });
            }

            return routes;
        },
        [alertsRoutes, buildAccountBadgeSlideoutComponent, settings.systemRoleMap, t, userState],
    );

    return { createGlobalMenuRoutes };
}

function createAnnouncementsRoutes(routes: GlobalAppRouteObject[], userState: UserState) {
    // Only admins and org members with 'manage-org-announcements' org permission have access to Announcements page
    if (requireAnySystemPermission(userState, 'manage-announcements') || requireAnyOrgPermission(userState, 'manage-org-announcements')) {
        routes.push({
            path: announcementsRoutes.form,
            element: <AnnouncementsForm />,
        });
    }
}

function createBackgroundCheckRoutes(routes: GlobalAppRouteObject[]) {
    routes.push({
        path: 'required-information/:accountId/',
        element: <BackgroundCheckRequiredInformationModal />,
    });
}
